import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ShoppingCart, Plus, Trash, X, CheckCircle, XCircle, HourglassSimple, Package, Truck, CheckSquare, Square, PencilSimple, FilePdf } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useSearch } from '../components/VerticalTabs';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRADES = [
  'Chef de chantier', 'Conducteur de travaux', 'Maçon', 'Électricien',
  'Plombier', 'Peintre', 'Carreleur', 'Menuisier', 'Charpentier',
  'Couvreur', 'Manoeuvre', 'Apprenti', 'Intérimaire', 'Autre'
];

const STATUS_CONFIG = {
  en_attente: { label: 'En attente', color: '#F57F17', bg: '#FFF8E1', icon: HourglassSimple },
  validee: { label: 'Validée', color: '#1565C0', bg: '#E3F2FD', icon: CheckCircle },
  commandee: { label: 'Commandée', color: '#EF6C00', bg: '#FFF3E0', icon: Package },
  livree: { label: 'Livrée', color: '#2E7D32', bg: '#E8F5E9', icon: Truck },
  annulee: { label: 'Annulée', color: '#C62828', bg: '#FFEBEE', icon: XCircle }
};

const ShoppingListPage = ({ embedded }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [lists, setLists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [filterProject, setFilterProject] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedList, setExpandedList] = useState(null);
  const [newItemText, setNewItemText] = useState('');
  const [newItemQty, setNewItemQty] = useState(1);
  const [newItemUnit, setNewItemUnit] = useState('');
  const [form, setForm] = useState({
    project_id: '', trade: '', notes: '',
    items: [{ description: '', quantity: 1, unit: '' }]
  });

  const fetchData = useCallback(async () => {
    try {
      const [listsRes, projectsRes] = await Promise.all([
        axios.get(`${API_URL}/api/shopping-lists`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects`, { withCredentials: true })
      ]);
      setLists(listsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.project_id || !form.trade) return toast.error('Chantier et corps de métier obligatoires');
    const validItems = form.items.filter(i => i.description.trim());
    if (validItems.length === 0) return toast.error('Ajoutez au moins un article');
    try {
      await axios.post(`${API_URL}/api/shopping-lists`, {
        project_id: form.project_id,
        trade: form.trade,
        items: validItems,
        notes: form.notes
      }, { withCredentials: true });
      toast.success('Liste de courses créée');
      setShowModal(false);
      setForm({ project_id: '', trade: '', notes: '', items: [{ description: '', quantity: 1, unit: '' }] });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleToggleItem = async (listId, itemId) => {
    try {
      await axios.put(`${API_URL}/api/shopping-lists/${listId}/items/${itemId}/toggle`, {}, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleAddItem = async (listId) => {
    if (!newItemText.trim()) return;
    try {
      await axios.post(`${API_URL}/api/shopping-lists/${listId}/items`, {
        description: newItemText, quantity: newItemQty || 1, unit: newItemUnit
      }, { withCredentials: true });
      setNewItemText('');
      setNewItemQty(1);
      setNewItemUnit('');
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleRemoveItem = async (listId, itemId) => {
    try {
      await axios.delete(`${API_URL}/api/shopping-lists/${listId}/items/${itemId}`, { withCredentials: true });
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleValidate = async (listId, action) => {
    try {
      await axios.put(`${API_URL}/api/shopping-lists/${listId}/validate`, { action }, { withCredentials: true });
      const labels = { validate: 'validée', order: 'commandée', deliver: 'livrée', cancel: 'annulée' };
      toast.success(`Liste ${labels[action]}`);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (listId) => {
    if (!window.confirm('Supprimer cette liste ?')) return;
    try {
      await axios.delete(`${API_URL}/api/shopping-lists/${listId}`, { withCredentials: true });
      toast.success('Liste supprimée');
      if (expandedList === listId) setExpandedList(null);
      fetchData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const addFormItem = () => {
    setForm({ ...form, items: [...form.items, { description: '', quantity: 1, unit: '' }] });
  };

  const updateFormItem = (index, field, value) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems });
  };

  const removeFormItem = (index) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const statusBadge = (status) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.en_attente;
    const Icon = cfg.icon;
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: cfg.bg, color: cfg.color }}>
        <Icon size={14} /> {cfg.label}
      </span>
    );
  };

  const { searchTerm } = useSearch();

  const filtered = lists.filter(l => {
    if (filterProject !== 'all' && l.project_id !== filterProject) return false;
    if (filterStatus !== 'all' && l.status !== filterStatus) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!(l.project_name || '').toLowerCase().includes(s) && !(l.trade || '').toLowerCase().includes(s) && !l.items?.some(i => (i.description || '').toLowerCase().includes(s))) return false;
    }
    return true;
  });

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div data-testid="shopping-list-page">
      {!embedded && (
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 className="page-title">Liste de courses</h1>
          <p className="page-subtitle">{lists.length} liste(s) au total</p>
        </div>
        <button data-testid="add-shopping-list-btn" className="btn btn-primary" onClick={() => setShowModal(true)}>
          <Plus size={18} weight="bold" /> Nouvelle liste
        </button>
      </div>
      )}
      {embedded && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
          <p style={{ color: '#5E5F66', fontSize: '0.9rem', margin: 0, alignSelf: 'center' }}>{lists.length} liste(s) au total</p>
          <button data-testid="add-shopping-list-btn" className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={18} weight="bold" /> Nouvelle liste
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <Select value={filterProject} onValueChange={setFilterProject}>
          <SelectTrigger style={{ width: '220px' }}><SelectValue placeholder="Filtrer par chantier" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les chantiers</SelectItem>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger style={{ width: '180px' }}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {filtered.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {filtered.map((sl) => {
            const isExpanded = expandedList === sl.id;
            const checkedCount = sl.items.filter(i => i.checked).length;
            return (
              <div key={sl.id} className="card" data-testid={`shopping-list-${sl.id}`}>
                <div style={{ padding: '16px 20px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }} onClick={() => setExpandedList(isExpanded ? null : sl.id)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <ShoppingCart size={24} weight="fill" style={{ color: '#002FA7' }} />
                    <div>
                      <div style={{ fontWeight: '700', fontSize: '1rem' }}>{sl.project_name}</div>
                      <div style={{ color: '#5E5F66', fontSize: '0.85rem' }}>
                        {sl.trade} - {sl.items.length} article(s) ({checkedCount}/{sl.items.length} cochés) - par {sl.created_by_name}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {statusBadge(sl.status)}
                    {isAdmin && (
                      <div style={{ display: 'flex', gap: '4px' }} onClick={(e) => e.stopPropagation()}>
                        {sl.status === 'en_attente' && (
                          <>
                            <button className="btn btn-primary btn-sm" onClick={() => handleValidate(sl.id, 'validate')} title="Valider" data-testid={`validate-list-${sl.id}`}>
                              <CheckCircle size={14} />
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleValidate(sl.id, 'cancel')} title="Annuler">
                              <XCircle size={14} />
                            </button>
                          </>
                        )}
                        {sl.status === 'validee' && (
                          <button className="btn btn-primary btn-sm" onClick={() => handleValidate(sl.id, 'order')} title="Marquer commandée">
                            <Package size={14} /> Commander
                          </button>
                        )}
                        {sl.status === 'commandee' && (
                          <button className="btn btn-primary btn-sm" onClick={() => handleValidate(sl.id, 'deliver')} title="Marquer livrée">
                            <Truck size={14} /> Livrée
                          </button>
                        )}
                        <button className="btn btn-secondary btn-sm" onClick={() => window.open(`${API_URL}/api/shopping-lists/${sl.id}/export/pdf`, '_blank')} title="Exporter PDF" data-testid={`export-pdf-list-${sl.id}`}>
                          <FilePdf size={14} />
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(sl.id)}>
                          <Trash size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {isExpanded && (
                  <div style={{ borderTop: '1px solid #E2E2E6', padding: '16px 20px' }}>
                    {sl.notes && (
                      <div style={{ backgroundColor: '#F4F5F7', padding: '8px 12px', borderRadius: '4px', marginBottom: '12px', fontSize: '0.85rem', color: '#5E5F66' }}>
                        {sl.notes}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      {sl.items.map((item) => (
                        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0', borderBottom: '1px solid #F4F5F7' }}>
                          <button
                            onClick={() => handleToggleItem(sl.id, item.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                            data-testid={`toggle-item-${item.id}`}
                          >
                            {item.checked
                              ? <CheckSquare size={22} weight="fill" color="#2E7D32" />
                              : <Square size={22} color="#9E9E9E" />
                            }
                          </button>
                          <span style={{
                            flex: 1, fontSize: '0.9rem',
                            textDecoration: item.checked ? 'line-through' : 'none',
                            color: item.checked ? '#9E9E9E' : '#0A0B10'
                          }}>
                            {item.description}
                          </span>
                          <span style={{ fontSize: '0.85rem', color: '#5E5F66', minWidth: '80px' }}>
                            x{item.quantity} {item.unit}
                          </span>
                          <button className="btn btn-danger btn-sm" onClick={() => handleRemoveItem(sl.id, item.id)} style={{ padding: '4px' }}>
                            <Trash size={12} />
                          </button>
                        </div>
                      ))}
                    </div>

                    {/* Add item inline */}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'flex-end' }}>
                      <input type="text" className="form-input" value={newItemText} onChange={(e) => setNewItemText(e.target.value)}
                        placeholder="Ajouter un article..." style={{ flex: 2 }}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddItem(sl.id); }}}
                      />
                      <input type="number" className="form-input" value={newItemQty} onChange={(e) => setNewItemQty(parseFloat(e.target.value) || 1)}
                        style={{ width: '70px' }} min="1" />
                      <input type="text" className="form-input" value={newItemUnit} onChange={(e) => setNewItemUnit(e.target.value)}
                        placeholder="unité" style={{ width: '90px' }} />
                      <button className="btn btn-primary btn-sm" onClick={() => handleAddItem(sl.id)} data-testid="add-inline-item">
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card">
          <div className="empty-state">
            <ShoppingCart size={48} className="empty-state-icon" />
            <p className="empty-state-text">Aucune liste de courses</p>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '4px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShoppingCart size={20} /> Nouvelle liste de courses
              </h3>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div className="form-group">
                  <label className="form-label">Chantier *</label>
                  <Select value={form.project_id || 'placeholder'} onValueChange={(v) => v !== 'placeholder' && setForm({ ...form, project_id: v })}>
                    <SelectTrigger data-testid="shopping-project-select"><SelectValue placeholder="Sélectionner un chantier" /></SelectTrigger>
                    <SelectContent>
                      {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <label className="form-label">Corps de métier *</label>
                  <Select value={form.trade || 'placeholder'} onValueChange={(v) => v !== 'placeholder' && setForm({ ...form, trade: v })}>
                    <SelectTrigger data-testid="shopping-trade-select"><SelectValue placeholder="Sélectionner un métier" /></SelectTrigger>
                    <SelectContent>
                      {TRADES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '16px' }}>
                <label className="form-label">Articles *</label>
                {form.items.map((item, idx) => (
                  <div key={item._key || `item-${idx}`} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'center' }}>
                    <input type="text" className="form-input" value={item.description}
                      onChange={(e) => updateFormItem(idx, 'description', e.target.value)}
                      placeholder="Description de l'article" style={{ flex: 2 }} required />
                    <input type="number" className="form-input" value={item.quantity}
                      onChange={(e) => updateFormItem(idx, 'quantity', parseFloat(e.target.value) || 1)}
                      style={{ width: '80px' }} min="1" step="1" />
                    <input type="text" className="form-input" value={item.unit}
                      onChange={(e) => updateFormItem(idx, 'unit', e.target.value)}
                      placeholder="unité" style={{ width: '90px' }} />
                    {form.items.length > 1 && (
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => removeFormItem(idx)} style={{ padding: '6px' }}>
                        <Trash size={14} />
                      </button>
                    )}
                  </div>
                ))}
                <button type="button" className="btn btn-secondary btn-sm" onClick={addFormItem} style={{ marginTop: '4px' }}>
                  <Plus size={14} /> Ajouter un article
                </button>
              </div>

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2} placeholder="Remarques (urgent, spécifications...)" />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>Annuler</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} data-testid="submit-shopping-list">Créer la liste</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShoppingListPage;
